/**
 * Pinterest Ads CSV Writer v1
 *
 * Generates CSV files compatible with Pinterest Bulk Editor.
 * Uses micro-currency format ($1 = 1,000,000 micros).
 *
 * @see https://help.pinterest.com/en/business/article/use-the-bulk-editor
 */

import { CampaignData, PlatformWriter, ValidationResult } from '../../types';

/**
 * Mapping from canonical objectives to Pinterest objective codes
 */
const PINTEREST_OBJECTIVES: Record<string, string> = {
  awareness: 'AWARENESS',
  brand_awareness: 'AWARENESS',
  reach: 'AWARENESS',
  traffic: 'CONSIDERATION',
  engagement: 'CONSIDERATION',
  video_views: 'CONSIDERATION',
  leads: 'CONVERSION',
  sales: 'CONVERSION',
  conversions: 'CONVERSION',
};

/**
 * Pinterest Bulk Editor CSV column headers
 */
const PINTEREST_HEADERS = [
  'Action',
  'Existing Campaign ID',
  'Campaign Name',
  'Campaign Status',
  'Campaign Objective',
  'Lifetime Spend Cap',
  'Ad Group Name',
  'Ad Group Status',
  'Ad Group Start Time',
  'Ad Group End Time',
  'Budget In Micro Currency',
  'Bid In Micro Currency',
  'Billable Event',
  'Optimization Goal',
  'Targeting GEO',
  'Targeting GENDER',
  'Targeting LOCALE',
  'Pin Title',
  'Pin Description',
  'Pin Link',
  'Pin Media URL',
  'Board ID',
];

export class PinterestWriterV1 implements PlatformWriter {
  readonly platform = 'pinterest' as const;
  readonly version = 'v1';
  readonly formatName = 'Pinterest Bulk Editor';

  validate(data: CampaignData): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Required fields
    if (!data.title) {
      errors.push('Campaign name is required');
    }
    if (!data.ad_copy?.link_url) {
      errors.push('Pin destination URL is required');
    }
    if (!data.media?.length) {
      errors.push('At least one media asset (image or video) is required');
    }

    // Pinterest-specific: Board ID required
    if (!data.platform_specific?.pinterest?.board_id) {
      errors.push(
        'Pinterest Board ID is required. Please select a board for your Pins.'
      );
    }

    // Budget minimums (Pinterest has strict minimums)
    if (data.budget?.daily && data.budget.daily < 5) {
      errors.push('Pinterest requires a minimum daily budget of $5');
    }
    if (data.budget?.lifetime && data.budget.lifetime < 25) {
      errors.push('Pinterest requires a minimum lifetime budget of $25');
    }
    if (!data.budget?.daily && !data.budget?.lifetime) {
      errors.push('Either daily or lifetime budget is required');
    }

    // Character limit validations
    if (data.ad_copy?.headline && data.ad_copy.headline.length > 100) {
      warnings.push(
        `Pin title is ${data.ad_copy.headline.length} characters. Maximum is 100. Will be truncated.`
      );
    }
    if (data.ad_copy?.body_text && data.ad_copy.body_text.length > 500) {
      warnings.push(
        `Pin description is ${data.ad_copy.body_text.length} characters. Maximum is 500. Will be truncated.`
      );
    }

    // Image aspect ratio check (Pinterest recommends 2:3)
    data.media?.forEach((asset) => {
      if (asset.type === 'image' && asset.width && asset.height) {
        const ratio = asset.width / asset.height;
        const idealRatio = 2 / 3; // 0.667
        if (Math.abs(ratio - idealRatio) > 0.2) {
          warnings.push(
            `Image has aspect ratio ${ratio.toFixed(2)}. Pinterest recommends 2:3 (0.67) for best display.`
          );
        }
      }
    });

    // Video aspect ratio check (Pinterest supports 9:16, 1:1, 16:9)
    data.media?.forEach((asset) => {
      if (asset.type === 'video' && asset.width && asset.height) {
        const ratio = asset.width / asset.height;
        const supportedRatios = [9 / 16, 1.0, 16 / 9];
        const isSupported = supportedRatios.some(
          (r) => Math.abs(ratio - r) < 0.1
        );
        if (!isSupported) {
          warnings.push(
            `Video aspect ratio ${ratio.toFixed(2)} may not display optimally. Pinterest supports 9:16, 1:1, or 16:9.`
          );
        }
      }
    });

    // Video duration check
    data.media?.forEach((asset) => {
      if (asset.type === 'video' && asset.duration_seconds) {
        if (asset.duration_seconds < 4) {
          errors.push('Pinterest requires videos to be at least 4 seconds');
        }
        if (asset.duration_seconds > 900) {
          errors.push(
            'Pinterest allows videos up to 15 minutes (900 seconds)'
          );
        }
      }
    });

    return { valid: errors.length === 0, errors, warnings };
  }

  transform(data: CampaignData): Record<string, string>[] {
    const rows: Record<string, string>[] = [];

    const objective =
      PINTEREST_OBJECTIVES[data.objective?.toLowerCase()] || 'AWARENESS';

    // Get board ID
    const boardId = data.platform_specific?.pinterest?.board_id || '';

    // Convert budget to micro-currency ($1 = 1,000,000)
    const dailyBudgetMicros = data.budget?.daily
      ? Math.round(data.budget.daily * 1_000_000).toString()
      : '5000000'; // Default $5

    const lifetimeBudgetMicros = data.budget?.lifetime
      ? Math.round(data.budget.lifetime * 1_000_000).toString()
      : '';

    // Default bid in micros ($2 = 2,000,000)
    const bidMicros = '2000000';

    // Convert dates to Unix timestamps (seconds)
    const startTimestamp = this.toUnixTimestamp(data.timeline?.start_date);
    const endTimestamp = data.timeline?.end_date
      ? this.toUnixTimestamp(data.timeline.end_date)
      : '';

    // Check if we have full campaigns structure
    const pinterestCampaigns = data.campaigns?.filter(c => c.platform === 'pinterest') || [];

    if (pinterestCampaigns.length > 0 && pinterestCampaigns.some(c => c.ad_groups && c.ad_groups.length > 0)) {
      // Use full campaigns structure - generate one row per ad variation (Pin)
      for (const campaign of pinterestCampaigns) {
        const campaignName = campaign.name || data.title;
        const campaignObjective = PINTEREST_OBJECTIVES[campaign.objective?.toLowerCase()] || objective;

        for (const adGroup of campaign.ad_groups || []) {
          // Parse targeting from ad group
          const adGroupTargeting = adGroup.targeting || {};
          const countries = adGroupTargeting.locations?.join(',') ||
            data.targeting?.locations?.map((l) => l.country).join(',') || 'US';

          // Gender formatting
          const genderStr = adGroupTargeting.gender?.toLowerCase();
          const gender = genderStr && genderStr !== 'all' ? genderStr : '';

          const locales = data.targeting?.languages?.join(',') || 'en-US';

          // Ad group budget
          const adGroupBudgetMicros = adGroup.budget
            ? Math.round(adGroup.budget * 1_000_000).toString()
            : dailyBudgetMicros;

          for (let adIdx = 0; adIdx < (adGroup.ad_variations?.length || 0); adIdx++) {
            const ad = adGroup.ad_variations[adIdx];

            // Get Pin content
            const pinTitle = ad.headlines?.[0] || data.ad_copy?.headline || campaignName;
            const pinDescription = ad.descriptions?.[0] || ad.description ||
              (Array.isArray(ad.primary_text) ? ad.primary_text[0] : ad.primary_text) ||
              data.ad_copy?.body_text || '';
            const pinLink = ad.link_url || ad.landing_page || data.ad_copy?.link_url || '';

            // Get media for this Pin
            let mediaUrl = '';
            if (ad.media && ad.media.length > 0) {
              mediaUrl = ad.media[0]?.public_url || '';
            } else {
              const mediaAsset = data.media?.[0];
              mediaUrl = mediaAsset?.public_url || '';
            }

            rows.push({
              Action: 'CREATE',
              'Existing Campaign ID': '',
              'Campaign Name': campaignName,
              'Campaign Status': 'ACTIVE',
              'Campaign Objective': campaignObjective,
              'Lifetime Spend Cap': lifetimeBudgetMicros,
              'Ad Group Name': adGroup.name || `${campaignName} - Ad Group`,
              'Ad Group Status': 'ACTIVE',
              'Ad Group Start Time': startTimestamp,
              'Ad Group End Time': endTimestamp,
              'Budget In Micro Currency': adGroupBudgetMicros,
              'Bid In Micro Currency': bidMicros,
              'Billable Event': 'IMPRESSION',
              'Optimization Goal': campaignObjective,
              'Targeting GEO': countries,
              'Targeting GENDER': gender,
              'Targeting LOCALE': locales,
              'Pin Title': this.truncate(pinTitle, 100),
              'Pin Description': this.truncate(pinDescription, 500),
              'Pin Link': pinLink,
              'Pin Media URL': mediaUrl,
              'Board ID': boardId,
            });
          }
        }
      }
    }

    // Fallback: If no rows generated from campaigns structure, use legacy single-row approach
    if (rows.length === 0) {
      const mediaAsset = data.media?.[0];
      const countries =
        data.targeting?.locations?.map((l) => l.country).join(',') || 'US';
      const gender = this.formatGender(data.targeting?.genders);
      const locales = data.targeting?.languages?.join(',') || 'en-US';

      rows.push({
        Action: 'CREATE',
        'Existing Campaign ID': '',
        'Campaign Name': data.title,
        'Campaign Status': 'ACTIVE',
        'Campaign Objective': objective,
        'Lifetime Spend Cap': lifetimeBudgetMicros,
        'Ad Group Name': `${data.title} - Ad Group`,
        'Ad Group Status': 'ACTIVE',
        'Ad Group Start Time': startTimestamp,
        'Ad Group End Time': endTimestamp,
        'Budget In Micro Currency': dailyBudgetMicros,
        'Bid In Micro Currency': bidMicros,
        'Billable Event': 'IMPRESSION',
        'Optimization Goal': objective,
        'Targeting GEO': countries,
        'Targeting GENDER': gender,
        'Targeting LOCALE': locales,
        'Pin Title': this.truncate(data.ad_copy?.headline || data.title, 100),
        'Pin Description': this.truncate(data.ad_copy?.body_text || '', 500),
        'Pin Link': data.ad_copy?.link_url || '',
        'Pin Media URL': mediaAsset?.public_url || '',
        'Board ID': boardId,
      });
    }

    return rows;
  }

  getHeaders(): string[] {
    return PINTEREST_HEADERS;
  }

  /**
   * Convert date string to Unix timestamp (seconds)
   */
  private toUnixTimestamp(dateStr?: string): string {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return Math.floor(date.getTime() / 1000).toString();
    } catch {
      return '';
    }
  }

  /**
   * Format gender targeting for Pinterest
   * Pinterest uses: male, female, unknown (for all)
   */
  private formatGender(genders?: string[]): string {
    if (!genders || genders.length === 0 || genders.includes('all')) {
      return ''; // Empty means all genders
    }

    return genders
      .map((g) => g.toLowerCase())
      .filter((g) => g === 'male' || g === 'female')
      .join(',');
  }

  /**
   * Truncate string to max length
   */
  private truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.slice(0, maxLength - 3) + '...';
  }
}
